import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  cancelLookup,
  cancelBooking,
  type CancelBookingInfo,
  type CancelLookupState,
  type CancelState,
} from '../../api/forms';
import { PRIVACY_URL } from '../../lib/constants';
import ThankYou from './ThankYou';

// Standalone-Ansicht für den Storno-Link aus der Bestätigungs-/Erinnerungs-Mail
// (?cancel=<token>). Wird statt des Formulars gerendert (siehe main.tsx).
// Ohne mindestens einen Grund ODER einen Kommentar ist keine Stornierung möglich.

const REASONS: { code: string; label: string }[] = [
  { code: 'time', label: 'Termin passt zeitlich doch nicht' },
  { code: 'no_interest', label: 'Kein Interesse mehr' },
  { code: 'other_studio', label: 'Anderes Studio / andere Sportart gewählt' },
];

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function CancelBooking({ token }: { token: string }) {
  const [lookup, setLookup] = useState<{ state: CancelLookupState | 'loading' | 'error'; booking?: CancelBookingInfo }>({
    state: 'loading',
  });
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CancelState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    cancelLookup(token)
      .then((r) => active && setLookup({ state: r.state, booking: r.booking }))
      .catch(() => active && setLookup({ state: 'error' }));
    return () => {
      active = false;
    };
  }, [token]);

  const toggle = (code: string) =>
    setReasons((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  // Pflicht: mindestens ein Grund ODER ein Freitext-Kommentar.
  const canSubmit = reasons.length > 0 || comment.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await cancelBooking(token, reasons, comment.trim());
      setResult(r.state);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  // Datenschutz-Hinweis inkl. automatischer Löschung nach Zweckbindung.
  const privacyNote = (
    <p className="rf-muted" style={{ fontSize: '0.8125rem', marginTop: '1rem' }}>
      Deine Angaben werden gemäß unserer{' '}
      <a className="rf-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">
        Datenschutzerklärung
      </a>{' '}
      nach Ablauf der Zweckbindung automatisch systemseitig gelöscht.
    </p>
  );

  // ── Ergebnis nach dem Absenden ───────────────────────────────────────────
  if (result === 'canceled') {
    return (
      <div className="rf-root">
        <ThankYou title="Termin storniert">
          <p>
            Schade, dass es diesmal nicht klappt – danke für deine Rückmeldung! Du bekommst gleich
            eine kurze Bestätigung per E-Mail. Wenn du magst, kannst du dir jederzeit einen neuen
            Termin buchen. Wir freuen uns auf dich!
          </p>
          {privacyNote}
        </ThankYou>
      </div>
    );
  }
  if (result === 'already_canceled') {
    return (
      <div className="rf-root">
        <div className="rf-alert rf-alert--info">
          Dieser Termin ist bereits storniert – du musst nichts weiter tun.
        </div>
      </div>
    );
  }
  if (result === 'not_found') {
    return (
      <div className="rf-root">
        <div className="rf-alert rf-alert--error">
          Der Storno-Link ist ungültig oder abgelaufen. Melde dich bei Fragen gern kurz bei uns.
        </div>
      </div>
    );
  }

  // ── Zustände aus dem ersten Laden (Lookup) ────────────────────────────────
  if (lookup.state === 'loading') {
    return (
      <div className="rf-root">
        <div className="rf-center">
          <Loader2 className="animate-spin" /> Einen Moment …
        </div>
      </div>
    );
  }
  if (lookup.state === 'not_active') {
    return (
      <div className="rf-root">
        <div className="rf-alert rf-alert--info">
          Dieser Termin ist nicht mehr aktiv – er wurde bereits storniert oder ist abgelaufen. Buche
          dir gern einfach einen neuen Termin.
        </div>
      </div>
    );
  }
  if (lookup.state === 'not_found' || lookup.state === 'error') {
    return (
      <div className="rf-root">
        <div className="rf-alert rf-alert--error">
          Der Storno-Link ist ungültig oder abgelaufen. Melde dich bei Fragen gern kurz bei uns.
        </div>
      </div>
    );
  }

  // ── lookup.state === 'ok': freundliche Storno-Seite mit Grund-Formular ─────
  const b = lookup.booking;
  return (
    <div className="rf-root">
      <div className="rf-card">
        <h2 className="rf-title" style={{ marginBottom: '0.5rem' }}>Schade, dass du absagen möchtest 😔</h2>
        <p className="rf-muted" style={{ marginBottom: '1rem' }}>
          Wir hätten dich gern kennengelernt. Bevor du deinen Termin stornierst: Sag uns kurz, warum
          es nicht passt – so können wir besser werden. Vielen Dank!
        </p>

        {b && (b.booking_date || b.school || b.sport) && (
          <div
            className="rf-field"
            style={{ background: 'var(--primary-light)', borderRadius: 'var(--radius-md)', padding: '0.8rem 1rem' }}
          >
            {b.sport && <div><strong>Kurs:</strong> {b.sport}</div>}
            {b.school && <div><strong>Standort:</strong> {b.school}</div>}
            {b.booking_date && <div><strong>Termin:</strong> {formatDate(b.booking_date)}</div>}
          </div>
        )}

        {error && <div className="rf-alert rf-alert--error">{error}</div>}

        <div className="rf-field">
          <label className="rf-label">Warum möchtest du absagen?</label>
          {REASONS.map((r) => (
            <label key={r.code} className="rf-check" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input type="checkbox" checked={reasons.includes(r.code)} onChange={() => toggle(r.code)} />
              <span className="rf-check__text">{r.label}</span>
            </label>
          ))}
        </div>

        <div className="rf-field">
          <label className="rf-label">Sonstiges / dein Kommentar</label>
          <textarea
            className="rf-input"
            rows={3}
            style={{ resize: 'vertical' }}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional – oder dein Grund, falls oben nichts passt"
          />
        </div>

        <button className="btn btn-primary btn-block" disabled={submitting || !canSubmit} onClick={() => void submit()}>
          {submitting ? <span className="rf-spinner" /> : 'Termin stornieren'}
        </button>
        {!canSubmit && (
          <p className="rf-muted" style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
            Bitte wähle mindestens einen Grund aus oder schreib uns kurz, warum – sonst können wir
            nicht stornieren.
          </p>
        )}

        {privacyNote}
      </div>
    </div>
  );
}
